import React, { useState, useEffect } from 'react';
import * as api from '../../services/api';
import {
    TrendingUp,
    Calendar,
    Clock,
    User,
    BookOpen,
    Trophy
} from 'lucide-react';
import './Admin.css';

export default function AdminResults() {
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAttempts = async () => {
            try {
                setLoading(true);
                const data = await api.getAllAttempts();
                setAttempts(data);
            } catch (err) {
                console.error('Fetch attempts error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAttempts();
    }, []);

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div className="spinner"></div></div>;

    return (
        <>
            <header className="admin-page-header">
                <div className="admin-page-header-text">
                    <h1>Performance Registry</h1>
                    <p>Granular oversight of student assessment metrics and academic velocity</p>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <div className="stat-card-base" style={{ padding: '0.75rem 1.5rem', aspectRatio: 'auto', minWidth: '160px', height: 'fit-content' }}>
                        <p className="stat-card-label" style={{ fontSize: '0.55rem' }}>Total Attempts</p>
                        <h4 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0.25rem 0' }}>{attempts.length}</h4>
                    </div>
                </div>
            </header >

            <div className="admin-table-section-card">
                <div className="admin-table-wrapper">
                    <table className="admin-standard-table">
                        <thead>
                            <tr>
                                <th>Assessment Timestamp</th>
                                <th>Student Identity</th>
                                <th>Exam Protocol</th>
                                <th>Metric Efficiency</th>
                                <th>Execution Status</th>
                                <th>Time Invested</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attempts.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '6rem 0', color: '#94a3b8', fontStyle: 'italic' }}>
                                        No academic execution records detected in the vault.
                                    </td>
                                </tr>
                            ) : (
                                attempts.slice().reverse().map(a => (
                                    <tr key={a._id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', color: '#64748b' }}>
                                                <Calendar size={14} />
                                                <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{new Date(a.completedAt).toLocaleDateString()}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <User size={14} color="#64748b" />
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95rem' }}>{a.userId?.fullName}</span>
                                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>{a.userId?.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                                                <BookOpen size={14} color="#066af2" />
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>{a.quizId?.title}</span>
                                                    <span style={{ fontSize: '0.625rem', fontWeight: 800, textTransform: 'uppercase', color: '#3b82f6', letterSpacing: '0.05em' }}>
                                                        {a.quizId?.track} TRACK
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '1.25rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.04em' }}>{a.percentage}%</span>
                                                    <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700 }}>{a.score} / {a.totalPoints} POINTS</span>
                                                </div>
                                                {a.passed && <Trophy size={16} color="#eab308" />}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-pill ${a.passed ? 'status-active' : 'status-inactive'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                {a.passed ? 'SUCCESS' : 'SUB-PAR'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, color: '#0f172a' }}>
                                                <Clock size={14} color="#94a3b8" />
                                                <span>{a.timeTaken} MIN</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
